/**
 * Página de gestión de Límites del Backoffice Operativo.
 * Gestión de reglas y asignación a merchants.
 *
 * @taskId T478
 * @see functional/Specs/specs_cycle_4.md HU-28-FE
 */

import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';
import { Button, Eyebrow, Tabs, TabsContent, TabsList, TabsTrigger } from '@flexibilitypay/ui';

import {
  LimitRulesTable,
  LimitRuleFormModal,
  MerchantAssignmentPanel,
  useLimitRules,
  useUpdateLimitRule,
  toLimitRuleSummary,
} from '../features/limits';
import type { LimitRuleSummary } from '../features/limits';

const PAGE_SIZE = 10;

export function LimitsPage() {
  const { t } = useTranslation(['limits', 'common']);
  const [searchParams, setSearchParams] = useSearchParams();

  // UI State
  const [selectedRuleForEdit, setSelectedRuleForEdit] = useState<LimitRuleSummary | null>(null);
  const [selectedRuleForAssign, setSelectedRuleForAssign] = useState<LimitRuleSummary | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // URL params
  const page = parseInt(searchParams.get('page') || '0', 10);
  const tab = searchParams.get('tab') || 'rules';

  // Query
  const {
    data: rulesData,
    isLoading,
    error,
    refetch,
  } = useLimitRules({
    page,
    size: PAGE_SIZE,
  });

  // Mapear items a view-model FE
  const ruleSummaries = useMemo(
    () => (rulesData?.items ?? []).map(toLimitRuleSummary),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rulesData?.items],
  );

  // Toggle mutation
  const toggleMutation = useUpdateLimitRule();

  // Handlers
  const handlePageChange = useCallback(
    (newPage: number) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set('page', String(newPage));
        return params;
      });
    },
    [setSearchParams],
  );

  const handleTabChange = useCallback(
    (value: string) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set('tab', value);
        params.set('page', '0');
        return params;
      });
    },
    [setSearchParams],
  );

  const handleCreate = useCallback(() => {
    setIsCreateModalOpen(true);
  }, []);

  const handleEdit = useCallback((rule: LimitRuleSummary) => {
    setSelectedRuleForEdit(rule);
  }, []);

  const handleAssign = useCallback((rule: LimitRuleSummary) => {
    setSelectedRuleForAssign(rule);
  }, []);

  const handleToggle = useCallback(
    (rule: LimitRuleSummary, newValue: boolean) => {
      toggleMutation.mutate({ id: rule.id, payload: { active: newValue } });
    },
    [toggleMutation],
  );

  const handleFormSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  return (
    <div className="container py-6">
      {/* Header */}
      <div className="mb-6">
        <Eyebrow>{t('limits:breadcrumb', 'Backoffice Operativo')}</Eyebrow>
        <h1 className="text-2xl font-bold">{t('limits:title', 'Gestión de Límites')}</h1>
        <p className="text-muted-foreground">
          {t('limits:subtitle', 'Configure y gestione los límites para las transacciones entrantes y salientes.')}
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={handleTabChange}>
        <TabsList className="mb-6">
          <TabsTrigger value="rules">
            {t('limits:tabs.rules', 'Gestión de Reglas')}
          </TabsTrigger>
          <TabsTrigger value="assign">
            {t('limits:tabs.assign', 'Asignar a Merchants')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="rules">
          {/* Error state */}
          {error && (
            <div className="mb-6 rounded-lg border border-destructive/50 bg-destructive/10 p-4">
              <div className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-5 w-5" />
                <span className="font-medium">
                  {t('limits:errors.loadFailed', 'Error al cargar reglas')}
                </span>
              </div>
              <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-3">
                {t('common:retry', 'Reintentar')}
              </Button>
            </div>
          )}

          {/* Tabla de reglas */}
          <LimitRulesTable
            rules={ruleSummaries}
            isLoading={isLoading}
            onEdit={handleEdit}
            onAssign={handleAssign}
            onToggle={handleToggle}
            onCreate={handleCreate}
            currentPage={page}
            totalPages={rulesData?.totalPages || 0}
            onPageChange={handlePageChange}
          />

          {/* Total */}
          {rulesData && rulesData.totalElements > 0 && (
            <p className="mt-4 text-sm text-muted-foreground">
              {t('limits:rules.total', 'Mostrando {{count}} de {{total}} reglas', {
                count: rulesData.items.length,
                total: rulesData.totalElements,
              })}
            </p>
          )}
        </TabsContent>

        <TabsContent value="assign">
          {/* Vista de asignación - lista de reglas para seleccionar */}
          <div className="space-y-4">
            <p className="text-muted-foreground">
              {t('limits:assign.instruction', 'Seleccione una regla para gestionar sus merchants asignados.')}
            </p>

            {isLoading ? (
              <div className="text-center py-8">
                {t('common:loading', 'Cargando...')}
              </div>
            ) : rulesData && rulesData.items.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {ruleSummaries.map((rule) => (
                  <div
                    key={rule.id}
                    className="border rounded-lg p-4 hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={() => handleAssign(rule)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && handleAssign(rule)}
                  >
                    <h3 className="font-medium">{rule.nombre}</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {t('limits:assign.scopeLabel', 'Aplica a:')} {rule.aplicaA}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {t('limits:assign.periodLabel', 'Período:')} {rule.periodo}
                    </p>
                    <Button variant="link" className="mt-2 p-0 h-auto">
                      {t('limits:assign.manage', 'Gestionar asignaciones →')}
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                {t('limits:assign.noRules', 'No hay reglas disponibles. Cree una primero.')}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Modal crear/editar regla */}
      <LimitRuleFormModal
        rule={selectedRuleForEdit}
        open={isCreateModalOpen || selectedRuleForEdit !== null}
        onCancel={() => {
          setIsCreateModalOpen(false);
          setSelectedRuleForEdit(null);
        }}
        onSuccess={handleFormSuccess}
      />

      {/* Panel de asignación */}
      <MerchantAssignmentPanel
        rule={selectedRuleForAssign}
        open={selectedRuleForAssign !== null}
        onClose={() => setSelectedRuleForAssign(null)}
      />
    </div>
  );
}
